/**
 * Utilities Index
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides centralized access to all utility functions
 * used throughout the ArbiWin application.
 */

// Import all utility modules
import formatters from './formatters';
import validators from './validators';
import helpers from './helpers';
import storage from './storage';
import performance from './performance';
import accessibility from './accessibility';
import { createError, handleHttpError, handleValidationError } from './errorHandler';
import { runDevelopmentTests } from './devTestRunner';

// Import new utilities from modules/utils
import {
  utils as moduleUtils,
  quickUtils as moduleQuickUtils,
  DateUtils,
  NumberUtils,
  ValidationUtils,
  BusinessLogic,
  Constants,
  Helpers
} from '../modules/utils';

/**
 * Centralized utilities object
 */
export const utils = {
  // Legacy utilities (existing)
  format: formatters,
  validate: validators,
  helpers: helpers,
  storage: storage,
  performance: performance,
  a11y: accessibility,
  error: {
    create: createError,
    handleHttp: handleHttpError,
    handleValidation: handleValidationError
  },

  // New utilities from modules/utils (Component 12)
  date: DateUtils,
  number: NumberUtils,
  validation: ValidationUtils,
  business: BusinessLogic,
  constants: Constants,
  helpersNew: Helpers
};

/**
 * Quick access to commonly used utilities
 */
export const quickUtils = {
  // Legacy formatting shortcuts
  formatCurrency: formatters.number.formatCurrency || NumberUtils.formatCurrency,
  formatDate: formatters.date.formatDate || DateUtils.formatDate,
  formatPercentage: formatters.number.formatPercentage || NumberUtils.formatPercentage,
  formatOdds: formatters.number.formatOdds || NumberUtils.formatOdds,

  // New formatting shortcuts (from modules/utils)
  formatBGN: NumberUtils.formatBGN,
  formatEUR: NumberUtils.formatEUR,
  formatDateTime: DateUtils.formatDateTime,
  formatTime: DateUtils.formatTime,
  getRelativeTime: DateUtils.getRelativeTime,

  // Legacy validation shortcuts
  validateEmail: validators.basic?.validateEmail || ValidationUtils.validateEmail,
  validatePassword: validators.basic?.validatePassword || ValidationUtils.validatePassword,
  validateStake: validators.surebet?.validateStake || ValidationUtils.validateStake,

  // New validation shortcuts (from modules/utils)
  validatePhoneBG: ValidationUtils.validatePhoneBG,
  validateOdds: ValidationUtils.validateOdds,
  validatePercentage: ValidationUtils.validatePercentage,

  // Legacy helper shortcuts
  debounce: helpers.function?.debounce || Helpers.debounce,
  throttle: helpers.function?.throttle || Helpers.throttle,
  deepClone: helpers.object?.deepClone || Helpers.deepClone,
  copyToClipboard: helpers.browser?.copyToClipboard || Helpers.copyToClipboard,

  // New helper shortcuts (from modules/utils)
  deepEqual: Helpers.deepEqual,
  deepMerge: Helpers.deepMerge,
  detectBrowser: Helpers.detectBrowser,
  detectDevice: Helpers.detectDevice,

  // Business logic shortcuts (from modules/utils)
  calculateMargin: BusinessLogic.calculateMargin,
  calculateStakeDistribution: BusinessLogic.calculateStakeDistribution,
  calculateROI: BusinessLogic.calculateROI,

  // Storage shortcuts
  storeAuthToken: storage.arbiwin.storeAuthToken,
  getAuthToken: storage.arbiwin.getAuthToken,
  removeAuthToken: storage.arbiwin.removeAuthToken,
  storeLanguage: storage.arbiwin.storeLanguage,
  getLanguage: storage.arbiwin.getLanguage,

  // Accessibility shortcuts
  announceToScreenReader: accessibility.aria.announceToScreenReader,
  setFocus: accessibility.focus.setFocus,
  trapFocus: accessibility.keyboard.trapFocus,

  // Performance shortcuts
  measureTime: helpers.performance?.measureTime || Helpers.measureTime,
  monitorApiCall: performance.metrics.monitorApiCall
};

/**
 * ArbiWin-specific utility functions
 */
export const arbiwinUtils = {
  /**
   * Process MarketsAnalysisDTO data for display
   * @param {Array} marketsAnalysisData - Raw API data
   * @param {string} language - Language code (bg/en)
   * @returns {Array} Processed surebet data
   */
  processSurebetData: function(marketsAnalysisData, language) {
    if (!Array.isArray(marketsAnalysisData)) return [];

    return marketsAnalysisData
      .filter(function(item) {
        // Only include items with marginFlag true (profitable)
        return item.marginFlag === true;
      })
      .map(function(item) {
        return formatters.data.formatSurebetData(item, language);
      })
      .filter(function(item) {
        return item !== null;
      })
      .sort(function(a, b) {
        // Sort by highest margin value
        const aMaxMargin = Math.max.apply(Math, Object.values(a.margins).map(function(m) { return m.value; }));
        const bMaxMargin = Math.max.apply(Math, Object.values(b.margins).map(function(m) { return m.value; }));
        return bMaxMargin - aMaxMargin;
      });
  },

  /**
   * Calculate arbitrage stakes distribution
   * @param {Array} coefficients - Array of coefficient objects
   * @param {number} totalStake - Total stake amount
   * @returns {Object} Stakes distribution result
   */
  calculateArbitrageStakes: function(coefficients, totalStake) {
    // Validate inputs
    const validation = validators.business.validateArbitrageInputs(coefficients, totalStake);
    if (!validation.isValid) {
      return {
        success: false,
        error: validation.error || validation.errorBg,
        stakes: [],
        profit: 0,
        margin: 0
      };
    }

    const odds = coefficients.map(function(coef) { return coef.value; });
    const stakes = helpers.math.calculateOptimalStakes(odds, totalStake);
    const margin = helpers.math.calculateArbitrageMargin(odds);
    const profit = totalStake * margin;

    const stakeDistribution = coefficients.map(function(coef, index) {
      const individualProfit = stakes[index] * coef.value;

      return {
        bookmaker: coef.bookmaker,
        bookmakerId: coef.bookmaker,
        outcome: coef.type,
        odds: coef.value,
        stake: stakes[index],
        profit: individualProfit,
        formattedStake: formatters.number.formatCurrency(stakes[index]),
        formattedOdds: formatters.number.formatOdds(coef.value),
        formattedProfit: formatters.number.formatCurrency(individualProfit)
      };
    });

    return {
      success: true,
      error: null,
      stakes: stakeDistribution,
      totalStake: totalStake,
      profit: helpers.math.roundToDecimals(profit, 2),
      margin: helpers.math.roundToDecimals(margin, 4),
      formattedProfit: formatters.number.formatCurrency(profit),
      formattedMargin: formatters.number.formatPercentage(margin)
    };
  },

  /**
   * Format stakes for clipboard copy
   * @param {Array} stakeDistribution - Stakes distribution array
   * @param {string} language - Language code (bg/en)
   * @returns {string} Formatted text for clipboard
   */
  formatStakesForClipboard: function(stakeDistribution, language) {
    if (!Array.isArray(stakeDistribution)) return '';

    language = language || 'bg';

    const lines = stakeDistribution.map(function(stake) {
      const bookmakerName = formatters.text.formatBookmakerName(stake.bookmakerId);
      const outcome = stake.outcome; // This should be localized in the calling component
      const amount = formatters.number.formatCurrency(stake.stake, language);

      if (language === 'bg') {
        return bookmakerName + ' - ' + outcome + ': ' + amount;
      } else {
        return bookmakerName + ' - ' + outcome + ': ' + amount;
      }
    });

    return lines.join('\n');
  },

  /**
   * Check if surebet data is stale
   * @param {Date|string} lastUpdate - Last update timestamp
   * @param {number} staleThresholdMs - Stale threshold in milliseconds
   * @returns {boolean} True if data is stale
   */
  isSurebetDataStale: function(lastUpdate, staleThresholdMs) {
    if (!lastUpdate) return true;

    staleThresholdMs = staleThresholdMs || 900000; // 15 minutes default
    const lastUpdateTime = lastUpdate instanceof Date ? lastUpdate.getTime() : new Date(lastUpdate).getTime();
    const now = Date.now();

    return (now - lastUpdateTime) > staleThresholdMs;
  },

  /**
   * Get next auto-refresh time
   * @param {Date|string} lastUpdate - Last update timestamp
   * @param {number} refreshIntervalMs - Refresh interval in milliseconds
   * @returns {Date|null} Next refresh time or null
   */
  getNextRefreshTime: function(lastUpdate, refreshIntervalMs) {
    if (!lastUpdate) return null;

    refreshIntervalMs = refreshIntervalMs || 600000; // 10 minutes default
    const lastUpdateTime = lastUpdate instanceof Date ? lastUpdate.getTime() : new Date(lastUpdate).getTime();

    return new Date(lastUpdateTime + refreshIntervalMs);
  }
};

/**
 * Initialize all utility systems
 * @param {Object} options - Initialization options
 * @returns {Object} Initialization results
 */
export function initializeUtils(options) {
  options = options || {};

  const results = {
    timestamp: new Date().toISOString(),
    storage: null,
    performance: null,
    accessibility: null,
    errors: []
  };

  try {
    // Initialize storage
    results.storage = storage.initialize();

    // Initialize performance monitoring
    if (options.performance !== false) {
      results.performance = performance.initialize(options.performance);
    }

    // Initialize accessibility features
    if (options.accessibility !== false) {
      results.accessibility = accessibility.initialize(options.accessibility);
    }

    // Log successful initialization
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Utilities initialized successfully:', {
        storage: !!results.storage,
        performance: !!results.performance,
        accessibility: !!results.accessibility,
        timestamp: results.timestamp
      });
    }

  } catch (error) {
    console.error('Utilities initialization failed:', error);
    results.errors.push(error.message);
  }

  return results;
}

/**
 * Export individual utility modules for direct import
 */
export {
  // Legacy modules
  formatters,
  validators,
  helpers,
  storage,
  performance,
  accessibility,
  runDevelopmentTests,

  // New modules from Component 12
  DateUtils,
  NumberUtils,
  ValidationUtils,
  BusinessLogic,
  Constants,
  Helpers,
  moduleUtils,
  moduleQuickUtils
};

/**
 * Export main utilities object as default
 */
export default utils;
